import { useLayout, useTranslation } from "@onefront/react-sdk";
import Stack from "@vapor/react-material/Stack";
import { format, subYears } from "date-fns";
import { useEffect, useState, useMemo, useCallback } from "react";
import { DocumentHeader } from "./document-components/DocumentHeader";
import { useGetAllowedMacrocategories } from "./hooks/api-hooks/useAllowedMacrocategories";
import { useGetDocuments } from "./hooks/api-hooks/useGetDocuments";
import {
  AiAsistant,
  UploadSmartComponent,
} from "@onefront/documents-smart-components";
import { useAuth } from "@onefront/react-sdk";
import { config } from "../../config";
import NoAccessError from "../common-components/NoAccessError";
import { useClientInformationStore } from "../store/useClientInformationStore";
import AiAssistantFloatingBubble from "./document-components/AiAssistantFloatingBubble";
import LeftPanelComponent from "../document-page/components/left-panel/LeftPanelComponent";
import PageTitle from "./document-components/PageTitle";
import { useGetToGetSpaceManagement } from "./hooks/api-hooks/useGetToGetSpaceManagement";
import { useDebounce } from "./hooks/useDebouncing";
import { useFilters } from "./hooks/useFilters";
import { checkIdExists } from "../../utils/checkIdExists";
import { useDocumentList } from "../document-page/state/useDocumentList";
import DocumentSlice from "../document-page/components/DocumentSlice";
import { Box, Slide } from "@vapor/react-material";
import DocumentTableHeader from "../document-page/components/DocumentTableHeader";
import SelectedDocumentActionHeader from "../document-page/components/table-headers/SelectedDocumentActionHeader";
import EmptyDocumentTableView from "./document-components/EmptyDocumentTableView";
import { useGetTrashDocs } from "./hooks/api-hooks/useGetTrashDocs";
import FilterComponent from "../document-page/components/filters/FilterComponent";
import { useScrollHandler } from "./document-utils/useScrollHandler";
import { defaultFilters } from "./document-utils/DefaultFiltersInputs";
import { useProcessMicroCategories } from "./document-utils/processMicroCategories";
import { useCheckboxHandler } from "./document-utils/handleCheckboxChange";
import { usePatchRestoreDocumentsFromBin } from "../../core/usecases/use-patch-restore-documents-from-trashbin/usePatchRestoreDocumentsFromBin";
import { useMoveDocumentsToBin } from "../../core/usecases/use-move-documents-to-bin/useMoveDocumentsToBin";
import { useDeleteDocuments } from "../../core/usecases/use-delete-documents/useDeleteDocuments";
import {
  leftComponentMainDiv,
  MainComponentPageStyled,
} from "./document-styled-components/DocumentPageComponentStyles";
import LoadingComponent from "./document-components/LoadingComponent";
import { BannerImageNotFound } from "./document-components/BannerImageNotFound";
import SnackBarAlert from "../common-components/SnackBarAlert";
import VaporPage from "@vapor/react-custom/VaporPage";
import { WorkspaceSelector } from "@drift/oneplatfront-components";

export const DocumentPage = () => {
  useEffect(() => {
    if ((window as any).aptrinsic) {
      (window as any).aptrinsic("track", "docs:ACCESS-LANDING-PAGE");
    }
  }, []);

  const { isEmbed } = useLayout();
  const { t } = useTranslation("DOCS");
  const [searchValue, setSearchValue] = useState("");
  const [selectedYear, setSelectedYear] = useState(
    t("document.datefilter.all")
  );
  const [monthType, setMonthType] = useState("month");
  const [months, setMonths] = useState([
    { name: t("document.dateFilter.all"), value: t("document.dateFilter.all") },
  ]);
  //page Size will be static for the moment
  const pageSize = 10;
  const [hasMore, setHasMore] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState({});
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const invoiceNumberDebounced = useDebounce(invoiceNumber);
  const [displayPage, setDisplayPage] = useState(true);
  const [checkIfUploaded, setCheckIfUploaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [openRestoreSnackBar, setOpenRestoreSnackBar] = useState(false);
  const [openRestoreManySnackBar, setOpenRestoreManySnackBar] = useState(false);
  const [openDeleteSnackBar, setOpenDeleteSnackBar] = useState(false);
  const [openMoveToBinSnackBar, setOpenMoveToBinSnackBar] = useState(false);

  const {
    selectedTenant: currentCompany,
    selectedWorkspace: ws,
    workspaceId,
    setWorkspaceId,
    tenantId,
  } = useClientInformationStore();

  const {
    clearSelectedDocuments,
    documentFilterOption,
    documentList,
    documentsSelected,
    filterByNewestDate,
    filterSettings,
    handleDocumentUpdate,
    incrementPageNumber,
    pageNumber,
    selectAllDocuments,
    setAllDocuments,
    setDocumentFilterOption,
    setDocumentList,
    setHasMoreDocuments,
    setOnFilterChange,
    setOnScroll,
    isFiltering,
  } = useDocumentList();

  const { data: dataMacrocategorie } = useGetAllowedMacrocategories(
    workspaceId,
    checkIfUploaded
  );
  const [macroCategoriesData, setMacroCategoriesData] = useState([]);
  const [allowAssistant, setAllowAssistant] = useState(
    checkIdExists(config.allowAssistant, tenantId)
  );
  const [sortedData, setSortedData] = useState(documentList);
  const [userCanUpload, setUserCanUpload] = useState(false);
  const subtitle = ws
    ? `${
        [...ws.companies, ...ws.accountants].find((x) => x.id === workspaceId)
          ?.description ?? ""
      }`
    : "";
  const auth = useAuth().tokenData;
  const user = {
    id: auth?.client_id,
    email: auth?.email,
    firstName: auth?.given_name,
    lastName: auth?.family_name,
  };
  const isAccountant = ws?.itemType === "ACCOUNTANT";
  const company = ws?.companies?.at(0);
  const accountant = ws?.accountants?.at(0);
  const userAccount = isAccountant ? accountant : company;
  const account = {
    id: userAccount?.id,
    name: userAccount?.description,
  };
  if ((window as any).aptrinsic) {
    (window as any).aptrinsic("identify", user, account);
  }

  const {
    data: spaceManagementData,
    loading: spaceManagementLoading,
    error: spaceManagementError,
  } = useGetToGetSpaceManagement({ workspaceId });

  const { fetch: restoreFromTrashbin, loading: restoreLoading } =
    usePatchRestoreDocumentsFromBin();

  const { fetch: moveDocumentsToBin, loading: isMoveToBinLoading } =
    useMoveDocumentsToBin();

  const { deleteDocuments, loading: isDeleteDocumentsLoading } =
    useDeleteDocuments();

  useEffect(() => {
    // Set documents when selectAllDocuments is true or clear them otherwise
    if (selectAllDocuments) {
      setAllDocuments(sortedData);
    } else {
      clearSelectedDocuments();
    }
  }, [
    selectAllDocuments,
    documentList,
    setAllDocuments,
    clearSelectedDocuments,
  ]);

  useEffect(() => {
    // SORT BY DATE LOGIC
    if (documentList) {
      const sorted = documentList?.sort((a, b) => {
        const dateA = new Date(a.createdOn).getTime();
        const dateB = new Date(b.createdOn).getTime();
        return filterByNewestDate ? dateA - dateB : dateB - dateA;
      });
      setSortedData([...sorted]);
    }
  }, [documentList, filterByNewestDate]);

  const memoizedDefaultFilters = useMemo(
    () => defaultFilters,
    [defaultFilters]
  );
  const [filters, setFilters, handleFilterChange] = useFilters(
    memoizedDefaultFilters
  );

  const { data, loading, hasLoaded, error } = useGetDocuments({
    workspaceId,
    searchTerm: searchValue,
    sort: filterSettings.sortOption,
    readByAccountant: filterSettings.readByAccountant,
    readByCompany: filterSettings.readByCompany,
    urgentDocs: filterSettings.urgentDocs,
    dateType: filterSettings.dateType,
    dateFilters: filterSettings.dateFilters,
    isUrgent: filterSettings.isUrgent,
    isFavourite: filterSettings.isFavourite,
    onlyVisibleToMe: filterSettings.onlyVisibleToMe,
    macroCategory:
      filterSettings.selectedChip === t("document.dateFilter.all")
        ? ""
        : filterSettings.selectedChip,
    microCategory: filterSettings.selectedMicro,
    selectedCategories,
    filterPerYear: filterSettings.filterPerYear,
    pageSize: pageSize,
    pageNumber,
    tags: filterSettings.tags,
    invoiceNumber: invoiceNumberDebounced,
    checkIfUploaded: checkIfUploaded,
    hasMore,
  });

  const { data: trashData } = useGetTrashDocs({
    checkIfUploaded: checkIfUploaded,
    documentFilterOption,
    hasMore,
    pageNumber,
    pageSize,
    workspaceId,
  });

  useEffect(() => {
    if (checkIfUploaded) {
      setCheckIfUploaded(false);
    }
    setAllowAssistant(checkIdExists(config.allowAssistant, tenantId));
    setMacroCategoriesData(dataMacrocategorie?.aggregationValue.macrocategory);
  }, [tenantId, dataMacrocategorie]);

  useEffect(() => {
    let updatedDocumentApiResponse;
    if (documentFilterOption === "CESTINO") {
      updatedDocumentApiResponse = trashData?.searchDocumentsResponse || [];
    } else {
      updatedDocumentApiResponse = data?.searchDocumentsResponse || [];
    }
    if (!loading && data) {
      setDocumentList(updatedDocumentApiResponse);
      if (data?.searchDocumentsResponse.length === 0) {
        setHasMoreDocuments(false);
      }
    }

    // set the hasMore to false or ture based in page number and docCount from below
    // if (documentFilterOption === "CESTINO") {
    //   setHasMore(false);
    // } else {
    //   setHasMore(data?.aggregationValue.macrocategory[0].docCount > pageNumber * pageSize);
    // }
  }, [data, pageNumber, trashData, documentFilterOption]);

  useEffect(() => {
    if (ws?.companies.length === 0 && ws?.accountants.length === 0) {
      setDisplayPage(false);
    } else {
      setDisplayPage(true);
    }
  }, [ws]);
  const canUpload = dataMacrocategorie?.permissions?.uploadWeb;

  useEffect(() => {
    setUserCanUpload(canUpload);
  }, [canUpload]);

  const macroCategories = useMemo(() => {
    return new Map(
      macroCategoriesData?.map((category) => [
        category?.bucketName,
        category?.docCount,
      ]) || []
    );
  }, [macroCategoriesData]);

  const allYears = useMemo(() => {
    return [...Array(10).keys()].map((n) =>
      format(subYears(new Date(), n), "yyyy")
    );
  }, []);

  useEffect(() => {
    setSelectedYear(t("document.dateFilter.all"));
    handleFilterChange("selectedMicro", []);
    setSelectedCategories({});
  }, [filterSettings.selectedChip]);

  useEffect(() => {
    setMonths([
      {
        name: t("document.dateFilter.all"),
        value: t("document.dateFilter.all"),
      },
    ]);
  }, [monthType, selectedYear]);

  useEffect(() => {
    if (months.length === 0) {
      setMonths([
        {
          name: t("document.dateFilter.all"),
          value: t("document.dateFilter.all"),
        },
      ]);
    }
  }, [months]);

  ////////////////////////////////////////Scroll Logic
  const handleScroll = useScrollHandler({
    incrementPageNumber,
    setOnScroll,
    setHasMore,
    hasMore,
    loading,
    data,
    pageNumber,
  });

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const handleClose = (event: any, reason: string) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  const handleDocumentRestore = useCallback(
    async (documentIds: string[]) => {
      const success = await restoreFromTrashbin(workspaceId, documentIds);
      if (success === 200) {
        setOpenRestoreSnackBar(true);
        handleDocumentUpdate(documentIds);
      }
    },
    [handleDocumentUpdate, restoreFromTrashbin, workspaceId]
  );

  const handleRestoreManyDocumentsFromBin = useCallback(async () => {
    const selectedDocumentIds = documentsSelected.map((doc) => doc.documentId);
    const success = await restoreFromTrashbin(workspaceId, selectedDocumentIds);
    if (success === 200) {
      setOpenRestoreManySnackBar(true);
      handleDocumentUpdate(selectedDocumentIds);
    }
  }, [handleDocumentUpdate, restoreFromTrashbin, workspaceId]);

  const handleDocumentsDelete = useCallback(
    async (documentIds?: string[]) => {
      const selectedDocumentIds = documentsSelected.map(
        (doc) => doc.documentId
      );
      const deleteDocumentIds =
        documentIds && documentIds.length > 0
          ? documentIds
          : selectedDocumentIds;
      const success = await deleteDocuments(workspaceId, deleteDocumentIds);
      if (success) {
        setOpenDeleteSnackBar(true);
        handleDocumentUpdate(deleteDocumentIds);
      }
    },
    [deleteDocuments, documentsSelected, handleDocumentUpdate, workspaceId]
  );

  const handleMoveSingleDocumentToBin = useCallback(
    async (documentId: string[]) => {
      const success = await moveDocumentsToBin(workspaceId, documentId);
      if (success === 200) {
        setOpenMoveToBinSnackBar(true);
        handleDocumentUpdate(documentId);
      }
    },
    [handleDocumentUpdate, moveDocumentsToBin, workspaceId]
  );

  const handleMioSpazioSnackLinkClick = () => {
    setOnFilterChange(true);
    setHasMoreDocuments(true);
    setDocumentFilterOption("IL_MIO_SPAZIO");
    handleFilterChange("isUrgent", false);
    handleFilterChange("isFavourite", false);
    handleFilterChange("onlyVisibleToMe", true);
  };

  const handleCestinoSnackLinkClick = () => {
    setOnFilterChange(true);
    setHasMoreDocuments(true);
    setDocumentFilterOption("CESTINO");
  };

  useProcessMicroCategories(data, filterSettings, handleFilterChange);

  const handleCheckboxChange = useCheckboxHandler(
    filterSettings,
    handleFilterChange
  );

  return ws?.companies?.length || ws?.accountants?.length ? (
    <>
      <SnackBarAlert
        alertMessage={t("snackBar.documentRestored")}
        linkMessage={t("snackBar.mySpace")}
        onClose={() => setOpenRestoreSnackBar(false)}
        onLinkClick={handleMioSpazioSnackLinkClick}
        open={openRestoreSnackBar}
        severity="success"
      />
      <SnackBarAlert
        alertMessage={t("snackBar.documentDeletedPermanently")}
        linkMessage={undefined}
        onClose={() => setOpenDeleteSnackBar(false)}
        onLinkClick={undefined}
        open={openDeleteSnackBar}
        severity="success"
      />
      <SnackBarAlert
        alertMessage={t("snackBar.documentMoveToBin")}
        linkMessage={t("snackBar.trashBin")}
        onClose={() => setOpenMoveToBinSnackBar(false)}
        onLinkClick={handleCestinoSnackLinkClick}
        open={openMoveToBinSnackBar}
        severity="success"
      />
      <SnackBarAlert
        alertMessage={t("snackBar.documentsRestored")}
        linkMessage={t("snackBar.mySpace")}
        onClose={() => setOpenRestoreManySnackBar(false)}
        onLinkClick={handleMioSpazioSnackLinkClick}
        open={openRestoreManySnackBar}
        severity="success"
      />
      <VaporPage
        headerLeft={<div style={{ width: "210px" }}></div>}
        headerRight={<WorkspaceSelector />}
        subtitle={
          t("document.subTitle") +
          `${
            ws
              ? `: ${
                  [...ws?.companies, ...ws?.accountants].find(
                    (x) => x.id === workspaceId
                  )?.description
                }`
              : ""
          }`
        }
        sx={{
          margin: "16px",
        }}
        title={t("common.title")}
      >
        {allowAssistant && (
          <AiAssistantFloatingBubble bottom={"10%"}>
            <AiAsistant
              withSideDrawer={true}
              workspaceId={workspaceId}
              userLanguage={ws.userLanguage}
            />
          </AiAssistantFloatingBubble>
        )}

        <DocumentHeader />
        {displayPage ? (
          <MainComponentPageStyled>
            {!currentCompany?.itemId ? (
              ""
            ) : (
              <div style={leftComponentMainDiv as React.CSSProperties}>
                <LeftPanelComponent
                  spaceManagementError={spaceManagementError}
                  freeSpace={spaceManagementData?.freeSpace}
                  totalSpace={spaceManagementData?.totalSpace}
                  usedSpace={spaceManagementData?.usedSpace}
                  setSearchValue={setSearchValue}
                  handleFilterChange={handleFilterChange}
                  documentFilterOption={documentFilterOption}
                />
              </div>
            )}
            <Box paddingRight="16px" paddingBottom="100px" width="100%">
              {displayPage ? (
                <FilterComponent
                  handleFilterChange={(name, value) => {
                    handleFilterChange(name, value);
                    setOnFilterChange(true);
                  }}
                  filters={filterSettings}
                  macroCategories={macroCategories}
                  data={data}
                  documentFilterOption={documentFilterOption}
                  filterMicroCategory={filterSettings.microCategory}
                  filterTags={filterSettings.tags}
                  handleCheckboxChange={handleCheckboxChange}
                  documentListLength={documentList.length}
                />
              ) : null}
              {/*Document table render components */}
              <div>
                {documentList?.length > 0 ? (
                  <>
                    <SelectedDocumentActionHeader
                      handleDocumentsDelete={handleDocumentsDelete}
                      handleRestoreManyDocumentsFromBin={
                        handleRestoreManyDocumentsFromBin
                      }
                      isDeleteDocumentsLoading={isDeleteDocumentsLoading}
                      restoreLoading={restoreLoading}
                    />
                    <DocumentTableHeader />
                    {documentList.map((doc) => (
                      <Stack
                        key={doc.documentId}
                        style={{ marginTop: "7px", marginBottom: "7px" }}
                      >
                        <DocumentSlice
                          doc={doc}
                          documentsApiLoading={loading}
                          handleDocumentsDelete={handleDocumentsDelete}
                          handleDocumentRestore={handleDocumentRestore}
                          handleMoveToBin={handleMoveSingleDocumentToBin}
                          isDeleteDocumentsLoading={isDeleteDocumentsLoading}
                          isMoveToBinLoading={isMoveToBinLoading}
                          restoreLoading={restoreLoading}
                          workspaceId={workspaceId}
                        />
                      </Stack>
                    ))}
                  </>
                ) : (
                  <EmptyDocumentTableView
                    documentsLength={documentList.length}
                    documentFilterOption={documentFilterOption}
                    filters={filterSettings}
                    searchValue={searchValue}
                  />
                )}
                {loading && hasMore && <LoadingComponent />}
                {/* {!hasMore && <p>No more documents to load</p>} */}
              </div>
            </Box>
          </MainComponentPageStyled>
        ) : (
          <BannerImageNotFound />
        )}
      </VaporPage>
    </>
  ) : (
    <>
      <PageTitle
        open={open}
        handleClose={handleClose}
        Slide={Slide}
        displayPage={displayPage}
        ws={ws}
        workspaceId={workspaceId}
        setWorkspaceId={setWorkspaceId}
      />
      <NoAccessError />
    </>
  );
};
